import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  TrendingUp, 
  ArrowLeft, 
  AlertTriangle, 
  Lightbulb, 
  Shield, 
  Globe,
  Loader2,
  Eye,
  Zap,
  Target,
  Users,
  Heart,
  Calendar,
  AlertCircle,
  Lock
} from "lucide-react";

// Pathway divider
function PathwayDivider() {
  return (
    <div className="my-20 flex items-center justify-center">
      <div className="h-px w-12 bg-stone-300 dark:bg-stone-700" />
      <div className="mx-3 w-1.5 h-1.5 rounded-full bg-stone-300 dark:bg-stone-700" />
      <div className="h-px w-12 bg-stone-300 dark:bg-stone-700" />
    </div>
  );
}

// Section header
function SectionHeader({ number, title }) {
  return (
    <div className="flex items-center gap-3 mb-8">
      <span className="text-xs text-stone-400 font-medium tracking-wider">
        {String(number).padStart(2, '0')}
      </span>
      <span className="text-stone-800 dark:text-stone-200 text-sm tracking-wide uppercase">
        {title}
      </span>
    </div>
  );
}

export default function VisionPanelPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVisionPanel();
  }, []);

  const loadVisionPanel = async () => {
    try {
      setLoading(true);
      const response = await api.admin.getVisionPanel();
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to load vision panel");
      }
      
      const jsonData = await response.json();
      setData(jsonData);
    } catch (err) {
      setError(err.message || "Failed to load vision panel");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-stone-400 mx-auto mb-4" />
          <p className="text-stone-500 text-sm">Loading Vision Panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center">
        <Card className="max-w-sm border-stone-200 dark:border-stone-800">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertTriangle className="w-10 h-10 text-stone-400 mx-auto mb-4" />
            <p className="text-stone-600 dark:text-stone-400 mb-6 text-sm">{error}</p>
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { thesis, closing, sections } = data;

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-[#1a4d3e] border-b border-[#0f3d2e]">
        <div className="max-w-3xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <Link to="/dashboard" className="text-white/60 hover:text-white transition-colors text-xs tracking-wide">
              ← Back to Dashboard
            </Link>
            <span className="text-white/40 text-xs tracking-wider uppercase">
              Admin Only
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white/80" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-xl text-white tracking-tight">Vision Panel</h1>
              <p className="text-white/50 text-xs tracking-wide">Strategic Narrative</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-16">
        
        {/* ===== OPENING THESIS ===== */}
        <section className="text-center mb-20" data-testid="vision-thesis">
          <p className="text-2xl text-stone-800 dark:text-stone-200 leading-relaxed mb-6 max-w-2xl mx-auto">
            {thesis.headline}
          </p>
          <p className="text-stone-500 text-sm">
            {thesis.subline}
          </p>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 1: THE PROBLEM ===== */}
        <section data-testid="vision-problem-section">
          <SectionHeader number={1} title={sections.problem.title} />
          
          <p className="text-stone-500 dark:text-stone-500 text-sm mb-8 leading-relaxed">
            {sections.problem.framing}
          </p>
          
          <p className="text-xl text-stone-800 dark:text-stone-200 mb-10 leading-relaxed">
            {sections.problem.headline}
          </p>
          
          <ul className="space-y-4 mb-10">
            {sections.problem.bullets.map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-4">
                <span className="w-1 h-1 rounded-full bg-stone-400 mt-2.5 flex-shrink-0" />
                <span className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed">{bullet}</span>
              </li>
            ))}
          </ul>
          
          <div className="bg-stone-100 dark:bg-stone-900/50 rounded p-6 border border-stone-200 dark:border-stone-800">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-4">Research Placeholders</p>
            <div className="space-y-3">
              {sections.problem.stat_placeholders.map((stat, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">{stat.label}</span>
                  <span className="text-xs text-stone-400 italic">{stat.value || "TBD"}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 2: THE INSIGHT ===== */}
        <section data-testid="vision-insight-section">
          <SectionHeader number={2} title={sections.insight.title} />
          
          <p className="text-xl text-stone-800 dark:text-stone-200 mb-4 leading-relaxed">
            {sections.insight.headline}
          </p>
          
          <p className="text-stone-500 dark:text-stone-500 text-sm mb-10 italic">
            {sections.insight.subline}
          </p>
          
          <div className="grid sm:grid-cols-2 gap-3">
            {sections.insight.key_points.map((point, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-stone-900 rounded border border-stone-200 dark:border-stone-800">
                <Lightbulb className="w-4 h-4 text-stone-400 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-stone-600 dark:text-stone-400 text-sm">{point}</span>
              </div>
            ))}
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 3: THE SOLUTION ===== */}
        <section data-testid="vision-solution-section">
          <SectionHeader number={3} title={sections.solution.title} />
          
          <p className="text-xl text-stone-800 dark:text-stone-200 mb-10 leading-relaxed">
            {sections.solution.headline}
          </p>
          
          <ul className="space-y-3 mb-10">
            {sections.solution.subpoints.map((point, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-[#1a4d3e]/60 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-stone-600 dark:text-stone-400 text-sm">{point}</span>
              </li>
            ))}
          </ul>
          
          <div className="bg-[#1a4d3e] rounded p-6 text-center">
            <p className="text-teal-300/60 text-xs uppercase tracking-wider mb-2">{sections.solution.callout.label}</p>
            <p className="text-white text-lg">{sections.solution.callout.value}</p>
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 4: DIFFERENTIATION ===== */}
        <section data-testid="vision-differentiation-section">
          <SectionHeader number={4} title={sections.differentiation.title} />
          
          <div className="bg-white dark:bg-stone-900 rounded border border-stone-200 dark:border-stone-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 dark:border-stone-800">
                  <th className="text-left py-4 px-5 text-xs text-stone-400 uppercase tracking-wider">
                    {sections.differentiation.columns.traditional}
                  </th>
                  <th className="text-left py-4 px-5 text-xs text-[#1a4d3e] uppercase tracking-wider">
                    {sections.differentiation.columns.ara_wealth}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sections.differentiation.rows.map((row, idx) => (
                  <tr key={idx} className="border-b border-stone-100 dark:border-stone-800 last:border-0">
                    <td className="py-4 px-5 text-stone-400 text-sm">
                      {row.traditional}
                    </td>
                    <td className="py-4 px-5 text-stone-700 dark:text-stone-300 text-sm">
                      {row.ara_wealth}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 5: DEFENSIBILITY (NEW) ===== */}
        <section data-testid="vision-defensibility-section">
          <SectionHeader number={5} title={sections.defensibility.title} />
          
          <ul className="space-y-4">
            {sections.defensibility.bullets.map((bullet, idx) => (
              <li key={idx} className="flex items-center gap-4">
                <Lock className="w-4 h-4 text-[#1a4d3e]/60 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-stone-600 dark:text-stone-400 text-sm">{bullet}</span>
              </li>
            ))}
          </ul>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 6: MARKET OPPORTUNITY ===== */}
        <section data-testid="vision-market-section">
          <SectionHeader number={6} title={sections.market_opportunity.title} />
          
          {/* Headline */}
          <p className="text-xl text-stone-800 dark:text-stone-200 mb-10 leading-relaxed">
            {sections.market_opportunity.headline}
          </p>
          
          {/* Key Points */}
          <ul className="space-y-3 mb-10">
            {sections.market_opportunity.key_points.map((point, idx) => (
              <li key={idx} className="flex items-start gap-4">
                <span className="w-1 h-1 rounded-full bg-stone-400 mt-2.5 flex-shrink-0" />
                <span className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
          
          {/* Target segments */}
          <div className="mb-10">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-4">Target Segments</p>
            <div className="flex flex-wrap gap-2">
              {sections.market_opportunity.target_segments.map((segment, idx) => (
                <Badge key={idx} variant="outline" className="text-xs border-stone-300 text-stone-500">
                  {segment}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Market size placeholders */}
          <div className="bg-stone-100 dark:bg-stone-900/50 rounded p-6 border border-stone-200 dark:border-stone-800 mb-6">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-4">Market Size Placeholders</p>
            <div className="space-y-3">
              {sections.market_opportunity.market_size_placeholders.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">{item.label}</span>
                  <span className="text-xs text-stone-400 italic">{item.value || "TBD"}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* TAM/SAM/SOM */}
          <div className="bg-stone-100 dark:bg-stone-900/50 rounded p-6 border border-stone-200 dark:border-stone-800">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-4">Market Sizing</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-stone-400 mb-1">{sections.market_opportunity.tam_sam_som.tam.label}</p>
                <p className="text-stone-600 dark:text-stone-400 text-sm">
                  {sections.market_opportunity.tam_sam_som.tam.value || <span className="italic text-stone-400">TBD</span>}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone-400 mb-1">{sections.market_opportunity.tam_sam_som.sam.label}</p>
                <p className="text-stone-600 dark:text-stone-400 text-sm">
                  {sections.market_opportunity.tam_sam_som.sam.value || <span className="italic text-stone-400">TBD</span>}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone-400 mb-1">{sections.market_opportunity.tam_sam_som.som.label}</p>
                <p className="text-stone-600 dark:text-stone-400 text-sm">
                  {sections.market_opportunity.tam_sam_som.som.value || <span className="italic text-stone-400">TBD</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 7: LIVE PERFORMANCE METRICS ===== */}
        <section data-testid="vision-metrics-section">
          <SectionHeader number={7} title={sections.live_metrics.title} />
          <p className="text-xs text-stone-400 mb-8">{sections.live_metrics.subtitle}</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Weekly Active Households */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.weekly_active_households.label}</span>
                </div>
                <p className="text-2xl text-stone-800 dark:text-stone-200">
                  {sections.live_metrics.metrics.weekly_active_households.value}
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  of {sections.live_metrics.metrics.weekly_active_households.total} ({sections.live_metrics.metrics.weekly_active_households.percentage}%)
                </p>
              </CardContent>
            </Card>

            {/* Reconcile Completion Rate */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.reconcile_completion_rate.label}</span>
                </div>
                <p className="text-2xl text-stone-800 dark:text-stone-200">
                  {sections.live_metrics.metrics.reconcile_completion_rate.value}%
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  {sections.live_metrics.metrics.reconcile_completion_rate.context}
                </p>
              </CardContent>
            </Card>

            {/* Average Streak Length */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.average_streak_length.label}</span>
                </div>
                <p className="text-2xl text-stone-800 dark:text-stone-200">
                  {sections.live_metrics.metrics.average_streak_length.value}
                </p>
                <p className="text-xs text-stone-400 mt-1">weeks</p>
              </CardContent>
            </Card>

            {/* Calm Mode Adoption */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.calm_mode_adoption.label}</span>
                </div>
                <p className="text-2xl text-stone-800 dark:text-stone-200">
                  {sections.live_metrics.metrics.calm_mode_adoption.value}%
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  {sections.live_metrics.metrics.calm_mode_adoption.context}
                </p>
              </CardContent>
            </Card>

            {/* Privacy Distribution */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.privacy_distribution.label}</span>
                </div>
                <div className="space-y-1">
                  {Object.entries(sections.live_metrics.metrics.privacy_distribution.value).map(([mode, count]) => (
                    <div key={mode} className="flex items-center justify-between">
                      <span className="text-sm text-stone-500 capitalize">{mode.replace(/_/g, ' ')}</span>
                      <span className="text-sm text-stone-700 dark:text-stone-300">{count}</span>
                    </div>
                  ))}
                  {Object.keys(sections.live_metrics.metrics.privacy_distribution.value).length === 0 && (
                    <p className="text-sm text-stone-400 italic">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Churn Risk */}
            <Card className="bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                  <span className="text-xs text-stone-400">{sections.live_metrics.metrics.churn_risk_summary.label}</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-500">Inactive 14+ days</span>
                    <span className="text-sm text-stone-700 dark:text-stone-300">{sections.live_metrics.metrics.churn_risk_summary.inactive_14_days}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-500">Push disabled</span>
                    <span className="text-sm text-stone-700 dark:text-stone-300">{sections.live_metrics.metrics.churn_risk_summary.push_disabled}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-stone-100 dark:border-stone-800 mt-2">
                    <span className="text-sm text-stone-500">Total at risk</span>
                    <span className="text-sm text-amber-600">{sections.live_metrics.metrics.churn_risk_summary.total_at_risk}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-xs text-stone-400">{sections.live_metrics.disclaimer}</p>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 8: LONG-TERM VISION ===== */}
        <section data-testid="vision-longterm-section">
          <SectionHeader number={8} title={sections.long_term_vision.title} />
          
          <p className="text-xl text-stone-800 dark:text-stone-200 mb-10 leading-relaxed">
            {sections.long_term_vision.headline}
          </p>
          
          <div className="bg-gradient-to-r from-[#1a4d3e]/5 to-transparent rounded p-6 border-l-2 border-[#1a4d3e]/30">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-4">Future Expansion</p>
            <ul className="space-y-3">
              {sections.long_term_vision.expansion_bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-stone-400 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-stone-600 dark:text-stone-400 text-sm">{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <PathwayDivider />

        {/* ===== SECTION 9: STRATEGIC POSITION ===== */}
        <section data-testid="vision-position-section">
          <SectionHeader number={9} title={sections.strategic_position.title} />
          
          <div className="space-y-10">
            {/* Categories */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-3">
                  {sections.strategic_position.categories.primary.label}
                </p>
                <Badge className="text-sm px-4 py-2 bg-[#1a4d3e] text-white border-0">
                  {sections.strategic_position.categories.primary.value}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-3">
                  {sections.strategic_position.categories.secondary.label}
                </p>
                <Badge variant="outline" className="text-sm px-4 py-2 border-[#1a4d3e] text-[#1a4d3e]">
                  {sections.strategic_position.categories.secondary.value}
                </Badge>
              </div>
            </div>
            
            {/* Core Promise */}
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-3">
                {sections.strategic_position.core_promise.label}
              </p>
              <p className="text-xl text-stone-800 dark:text-stone-200">
                "{sections.strategic_position.core_promise.value}"
              </p>
            </div>
            
            {/* Brand Ethos */}
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-3">
                {sections.strategic_position.brand_ethos.label}
              </p>
              <p className="text-stone-500 dark:text-stone-500 italic">
                "{sections.strategic_position.brand_ethos.value}"
              </p>
            </div>
          </div>
        </section>

        {/* ===== CLOSING STATEMENT ===== */}
        <div className="mt-20 pt-12 border-t border-stone-200 dark:border-stone-800 text-center">
          <p className="text-stone-600 dark:text-stone-400 text-sm italic max-w-lg mx-auto">
            {closing.statement}
          </p>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center">
          <p className="text-xs text-stone-400">
            Generated {new Date(data.generated_at).toLocaleString()}
          </p>
          <p className="text-xs text-stone-400 mt-1">
            ARA Wealth — Confidential
          </p>
        </footer>

      </main>
    </div>
  );
}
